import { forwardRef, useImperativeHandle, useRef } from 'react'
import { useGSAP } from '@gsap/react'
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/dist/ScrollTrigger'

import {
  MASK_TRANSITION_ENTER_DURATION,
  MASK_TRANSITION_LEAVE_DURATION,
  MASK_TRANSITION_STAGGER_AMOUNT
} from '@/contexts/transition'
import { useLoaderStore } from '@/store/loader'

export type MaskTransitionHandler = {
  enterTimeline: () => gsap.core.Timeline
  leaveTimeline: () => gsap.core.Timeline
}

const MaskTransition = forwardRef<MaskTransitionHandler>((_, ref) => {
  const root = useRef<HTMLDivElement>(null)
  const isPreloaderPlayed = useLoaderStore((state) => state.isPreloaderPlayed)

  const { contextSafe } = useGSAP({ scope: root })

  const enterTimeline = contextSafe(() => {
    return gsap
      .timeline()
      .set(root.current, { display: 'block' })
      .add(
        gsap.timeline().fromTo(
          ['[data-mask-primary]', '[data-mask-secondary]'],
          { yPercent: 100 },
          {
            yPercent: 0,
            duration: MASK_TRANSITION_ENTER_DURATION,
            stagger: MASK_TRANSITION_STAGGER_AMOUNT,
            ease: 'power3.in',
            force3D: true
          }
        )
      )
      .add(
        gsap
          .timeline()
          .fromTo(
            '[data-mask-transparent]',
            { yPercent: 100 },
            {
              yPercent: 0,
              duration: MASK_TRANSITION_ENTER_DURATION,
              ease: 'power3.in',
              force3D: true
            }
          )
          .fromTo(
            '[data-mask-symbol]',
            { yPercent: -100 },
            {
              yPercent: 0,
              duration: MASK_TRANSITION_ENTER_DURATION,
              ease: 'power3.in',
              force3D: true
            },
            '<'
          ),
        `<+${MASK_TRANSITION_ENTER_DURATION}`
      )
  })

  const leaveTimeline = contextSafe(() => {
    return gsap
      .timeline({
        onComplete: () => {
          if (!isPreloaderPlayed) {
            useLoaderStore.setState({ isPreloaderPlayed: true })
            gsap.delayedCall(1, () => {
              ScrollTrigger.refresh()
            })
          }
        }
      })
      .add(
        gsap
          .timeline()
          .fromTo(
            '[data-mask-transparent]',
            { yPercent: 0 },
            {
              yPercent: -100,
              duration: MASK_TRANSITION_LEAVE_DURATION,
              ease: 'power3.out',
              force3D: true
            }
          )
          .fromTo(
            '[data-mask-symbol]',
            { yPercent: 0 },
            {
              yPercent: 100,
              duration: MASK_TRANSITION_LEAVE_DURATION,
              ease: 'power3.out',
              force3D: true
            },
            '<'
          ),
        isPreloaderPlayed ? 0 : 1.5
      )
      .add(
        gsap.timeline().fromTo(
          ['[data-mask-secondary]', '[data-mask-primary]'],
          { yPercent: 0 },
          {
            yPercent: -100,
            duration: MASK_TRANSITION_LEAVE_DURATION,
            stagger: MASK_TRANSITION_STAGGER_AMOUNT,
            ease: 'power3.out',
            force3D: true
          }
        ),
        `<+${MASK_TRANSITION_STAGGER_AMOUNT}`
      )
      .set(root.current, { display: 'none' })
  })

  useImperativeHandle(
    ref,
    () => ({
      leaveTimeline,
      enterTimeline
    }),
    [enterTimeline, leaveTimeline]
  )

  return (
    <div
      ref={root}
      className="fixed left-0 top-0 z-transition h-screen w-full"
      data-mask-transition
    >
      <div
        className="absolute left-0 top-0 h-full w-1/2 bg-black"
        data-mask-primary
      />
      <div
        className="absolute right-0 top-0 h-full w-1/2 bg-black"
        data-mask-secondary
      />
      <div
        className="absolute left-0 top-0 h-full w-full overflow-hidden"
        data-mask-transparent
      >
        <div
          className="h-dvh-vh absolute left-0 top-0 flex w-full items-center justify-center bg-black"
          data-mask-loader
        />
      </div>
    </div>
  )
})

export default MaskTransition
